import React, { useEffect, useMemo, useState } from 'react';
import DynamicTable from '@components/dynamic-table/dynamic-table.tsx';
import createTableStore from '@store/table-store/table.store.ts';
import { ViewControlsEnum } from '@models/view-controls.model.ts';
import { playlistsColumns } from './playlists-table-component/columns/columns.tsx';
import { Button } from '@mui/material';
import AppDialog from '@shared/components/app-dialog/app-dialog.tsx';
import PlaylistDialogForm from '../../../../../../shared/components/playlist-dialog/playlist-dialog-forms.tsx';
import DeleteDialog from '@shared/components/delete-dialog/delete-dialog.tsx';
import { useTranslation } from 'react-i18next';
import usePlaylistsStore from '../../store/playlists.store.ts';
import { ICONS } from '@shared/components/icons/icon.tsx';
import MediaModal from '@shared/components/media-modal/media-modal.tsx';
import { FileUploadType } from '@shared/models/file-upload.models.ts';
import { PlaylistsRowDataProps } from '@modules/entertainment/models/playlists.model.ts';
import { getPlaylists } from '@shared/api/entertainment-api/playlists-api.ts';
import { useSearchParams } from 'react-router-dom';
import { SortOrder, TriggerTypeEnum } from '@shared/models/table.model.ts';
import { TABLE_HEIGHT } from '@utils/table-height.utils.ts';

const PlaylistsTable: React.FC = () => {
  const { deleteRow, editRow, createRow, isLoading } = usePlaylistsStore();
  const { t } = useTranslation();
  const tableStore = useMemo(() => createTableStore(), []);
  const [view, setView] = useState(ViewControlsEnum.List);
  const [imageSrc, setImageSrc] = useState<{ src: string } | null>(null);

  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
  const [selectedItemId, setSelectedItemId] = useState<string>('');
  const [selectedItem, setSelectedItem] =
    useState<PlaylistsRowDataProps | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const handleEdit = (row: any) => {
    setOpenDialog(true);
    setSelectedItem(row);
  };

  const handleDelete = (row: any) => {
    setShowDeleteDialog(true);
    setSelectedItemId(row?._id || null);
  };

  const handleDeleteConfirm = () => {
    if (!selectedItemId) return;

    deleteRow({
      id: selectedItemId,
      state: tableStore.getState(),
      handleClose
    });
  };

  const handleCreateOrUpdate = async (row: any) => {
    if (row?._id) {
      await editRow({ data: row, state: tableStore.getState(), handleClose });
    } else {
      await createRow({ data: row, state: tableStore.getState(), handleClose });
    }
  };

  const handleClose = () => {
    setShowDeleteDialog(false);
    setOpenDialog(false);
    setSelectedItem(null);
  };

  const handleTableTrigger = (type: TriggerTypeEnum) => {
    if (type === TriggerTypeEnum.Add) {
      setOpenDialog(true);
    }
  };

  const onCloseImageModal = () => {
    setImageSrc(null);
  };

  const columns = playlistsColumns(handleEdit, handleDelete);

  useEffect(() => {
    const fetchDataAndOpenDialog = async () => {
      const dialog = searchParams.get('dialog');
      if (dialog === 'open') {
        setOpenDialog(true);
      }
    };

    fetchDataAndOpenDialog().then();
  }, []);

  return (
    <div className="w-full h-full">
      <div className="flex justify-end mb-4">
        <Button
          variant="contained"
          color="primary"
          startIcon={ICONS.plusIcon}
          onClick={() => handleEdit(selectedItem)}
        >
          {t('add')}
        </Button>
      </div>

      <div className={TABLE_HEIGHT.WITH_TABS_AND_ACTIONS}>
        {view === ViewControlsEnum.List && (
          <DynamicTable
            tableStore={tableStore}
            apiFunction={getPlaylists}
            columns={columns}
            tableKey="entertainment-playlists-table"
            onTriggerAction={handleTableTrigger}
            initialState={{
              sort: ['createdAt', SortOrder.Descending]
            }}
          />
        )}
      </div>

      <div className="w-full h-full">
        <AppDialog
          open={openDialog}
          size="sm"
          title={t('entertainment_add_playlist_title')}
          content={
            <PlaylistDialogForm
              selectedItem={selectedItem}
              setSelectedItem={setSelectedItem}
              handleClose={handleClose}
              handleCreateOrUpdate={handleCreateOrUpdate}
            />
          }
          onClose={handleClose}
        />

        <DeleteDialog
          onCancel={handleClose}
          onDelete={handleDeleteConfirm}
          open={showDeleteDialog}
          isLoading={isLoading()}
          title={t('delete_popup_title')}
        />

        <MediaModal
          type={FileUploadType.Image}
          selectedItem={imageSrc}
          showDialog={imageSrc ? true : false}
          onClose={onCloseImageModal}
        />
      </div>
    </div>
  );
};

export default PlaylistsTable;
